import { Chart, registerables } from 'chart.js';
import { toFont, toPadding } from 'chart.js/helpers';
import ChartDataLabels from 'chartjs-plugin-datalabels';


const DATA_LABEL_OFFSET = 2;
const DATA_LABEL_PADDING = 2;
const DATA_LABEL_SAFE_SPACE = 2;

// Matches the < 768px breakpoint the font sizes switch at
const mobileQuery = window.matchMedia('(max-width: 767px)');

function isMobile() {
    return mobileQuery.matches;
}

export default function handleChartsBlocks() {
    const $charts = $('.js-report-chart');

    if ($charts.length > 0) {
        Chart.register(...registerables);
        Chart.register(ChartDataLabels);

        const pendingCharts = new Map();
        const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting || !pendingCharts.has(entry.target)) {
                    return;
                }

                const render = pendingCharts.get(entry.target);
                pendingCharts.delete(entry.target);
                observer.unobserve(entry.target);
                render();
            });
        }, {
            threshold: 0.5,
            rootMargin: '0px 0px 0px 0px',
        }) : null;

        $charts.each(function () {
            const $chart = $(this);

            const rawText = $chart.find('.js-report-chart-data').text();

            try {
                const data = JSON.parse(rawText);

                if (!data || data.length <= 0 || data.labels.length <= 0 || data.values.length <= 0) {
                    throw new Error('Could not load data for chart');
                }

                const $canvas = $chart.find('.js-report-chart-container');
                const canvas = $canvas[0];

                if (!canvas) {
                    return;
                }

                if (!observer) {
                    handleChart($chart, data);
                    return;
                }

                // Watch the wrapper rather than the canvas. Before Chart.js sizes it, a fixed height canvas is as wide as
                // its default 2:1 shape (e.g. 800px for 400px tall), which on mobile is mostly clipped by the wrapper
                // so it never reaches the threshold
                pendingCharts.set(this, () => handleChart($chart, data));
                observer.observe(this);
            } catch (e) {
                console.log(e);
            }
        });
    }
}

function handleChart($element, data) {
    // Wait for the web fonts so the labels are measured with the font they're drawn in
    const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();

    fontsReady.then(() => renderChart($element, data));
}

function renderChart($element, data) {
    const $container = $element.find('.js-report-chart-container');
    const ctx = $container[0];

    const {
        values,
        colors,
        labels,
        direction,
        heightMode,
        barWidthMode,
        barWidth,
        labelPrefix,
        labelSuffix,
    } = data;

    const isHorizontalAuto = heightMode === 'auto' && direction !== 'vertical';

    // 1) Start at zero so there is a visible transition.
    const zeroValues = values.map(() => 0);

    const chart = new Chart(ctx, {
        type: 'bar',
        data: {
            labels: labels,
            datasets: [
                {
                    label: 'Source',
                    data: zeroValues,
                    datalabels: {
                        align: 'end',
                        anchor: 'end',
                    },
                    borderWidth: 0,
                    backgroundColor: colors,
                    barThickness: barWidthMode === 'flex' ? 'flex' : barWidth,
                },
            ],
        },
        plugins: [
            ChartDataLabels,
            createAutoHeightPlugin(() => isHorizontalAuto, (height) => {
                ctx.parentNode.style.height = `${height}px`;
            }),
        ],
        options: {
            animation: {
                duration: 1200,
                easing: 'easeOutQuart',
            },
            transitions: {
                active: {
                    animation: {
                        duration: 1200,
                    },
                },
            },
            layout: {
                // Uses the final values (not the zeroed ones) so the padding doesn't jump as the bars animate in
                padding: ({chart}) => calculateChartPadding(chart, data),
            },
            responsive: true,
            // Horizontal auto height graphs size their container themselves (see createAutoHeightPlugin)
            maintainAspectRatio: heightMode === 'auto' && !isHorizontalAuto,
            indexAxis: direction === 'vertical' ? 'x' : 'y',
            scales: {
                ...generateChartScales(direction),
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                },
                datalabels: {
                    color: '#000',
                    font: () => generateDataLabelFontStyles(direction),
                    formatter: (value) => formatDataLabel(value, labelPrefix, labelSuffix),
                },
            },
        },
    });

    // 2) Next frame, animate to real values.
    requestAnimationFrame(() => {
        chart.data.datasets[0].data = values;
        chart.update();
    });

    // Crossing the breakpoint changes the font sizes, which doesn't always resize the canvas (e.g. in a fixed width
    // container), so lay the chart out again with the new sizes
    mobileQuery.addEventListener('change', () => chart.update('none'));
}

function formatDataLabel(value, labelPrefix, labelSuffix) {
    return `${labelPrefix || ''}${value}${labelSuffix || ''}`;
}

/**
 * Works out how much room the data labels need outside the bars so they never get cut off
 * at the edge of the canvas. Used as a scriptable `layout.padding`, so it's recalculated
 * on every layout (resize, data change, etc.).
 */
function calculateChartPadding(chart, {values, direction, labelPrefix, labelSuffix}) {
    const padding = {top: 0, right: 0, bottom: 0, left: 0};

    if (!values || values.length <= 0) {
        return padding;
    }

    const font = toFont(generateDataLabelFontStyles(direction));
    const ctx = chart.ctx;

    ctx.save();
    ctx.font = font.string;
    const labelWidths = values.map((value) =>
        measureText(ctx, formatDataLabel(value, labelPrefix, labelSuffix)) + DATA_LABEL_PADDING * 2,
    );
    ctx.restore();

    const labelHeight = font.lineHeight + DATA_LABEL_PADDING * 2;

    if (direction === 'vertical') {
        // Labels sit above the bars
        padding.top = labelHeight + DATA_LABEL_OFFSET + DATA_LABEL_SAFE_SPACE;

        // The first/last labels can overhang the sides when they're wider than the bar's slot
        const halfSlot = chart.width / values.length / 2;
        padding.left = Math.max(0, labelWidths[0] / 2 - halfSlot);
        padding.right = Math.max(0, labelWidths[labelWidths.length - 1] / 2 - halfSlot);

        return padding;
    }

    // Labels sit to the right of the bars
    padding.right = Math.max(...labelWidths) + DATA_LABEL_OFFSET + DATA_LABEL_SAFE_SPACE;

    // The top/bottom labels can overhang when they're taller than the bar's slot
    const halfSlot = chart.height / values.length / 2;
    padding.top = Math.max(0, labelHeight / 2 - halfSlot);
    padding.bottom = padding.top;

    return padding;
}

// Axis labels are wrapped to fit the space they have rather than being cut off
const HORIZONTAL_TICK_MAX_WIDTH = 0.4;
const VERTICAL_TICK_GAP = 8;

function getAvailableWidth(chart) {
    const padding = toPadding(chart.options.layout.padding);
    return Math.max(0, chart.width - padding.left - padding.right);
}

function wrapTickLabel(scale, value, maxWidth) {
    const label = scale.getLabelForValue(value);
    // Keep any line breaks added in the editor and wrap each line on its own
    const lines = Array.isArray(label) ? label : [label];
    const ctx = scale.ctx;

    ctx.save();
    ctx.font = toFont(scale.options.ticks.font).string;
    const wrapped = lines.flatMap((line) => wrapText(ctx, String(line), maxWidth));
    ctx.restore();

    return wrapped;
}

// Text widths only depend on the font and the text, so they're cached rather than measured again on every layout
const textWidthCache = new Map();

function measureText(ctx, text) {
    const key = `${ctx.font}|${text}`;
    let width = textWidthCache.get(key);

    if (width === undefined) {
        width = ctx.measureText(text).width;
        textWidthCache.set(key, width);
    }

    return width;
}

function wrapText(ctx, text, maxWidth) {
    const lines = [];
    let line = '';

    text.split(/\s+/).filter(Boolean).forEach((word) => {
        const candidate = line ? `${line} ${word}` : word;

        if (measureText(ctx, candidate) <= maxWidth) {
            line = candidate;
            return;
        }

        if (line) {
            lines.push(line);
        }

        // A single word that's too long on its own gets hyphenated across lines
        const parts = splitWord(ctx, word, maxWidth);
        line = parts.pop();
        lines.push(...parts);
    });

    if (line) {
        lines.push(line);
    }

    return lines.length ? lines : [text];
}

function splitWord(ctx, word, maxWidth) {
    const parts = [];
    let part = '';

    for (const char of word) {
        if (part && measureText(ctx, `${part}${char}-`) > maxWidth) {
            parts.push(`${part}-`);
            part = char;
        } else {
            part += char;
        }
    }

    parts.push(part);

    return parts;
}

// Horizontal graphs on auto height grow taller when the rows need more room than the default 2:1 shape gives them
const DEFAULT_ASPECT_RATIO = 2;
const ROW_GAP = 8;

/**
 * Works out the height a horizontal graph needs so every row has room for its (wrapped) axis label,
 * its data label and its bar.
 */
function calculateHorizontalChartHeight(chart) {
    const scale = chart.scales.y;
    const labels = chart.data.labels || [];
    const maxLabelWidth = getAvailableWidth(chart) * HORIZONTAL_TICK_MAX_WIDTH;
    const tickLineHeight = toFont(scale.options.ticks.font).lineHeight;
    const dataLabelHeight = toFont(generateDataLabelFontStyles('horizontal')).lineHeight + DATA_LABEL_PADDING * 2;
    const barThickness = chart.data.datasets[0] ? chart.data.datasets[0].barThickness : null;
    const barHeight = typeof barThickness === 'number' ? barThickness : 0;

    const rowsHeight = labels.reduce((total, label, index) => {
        const lineCount = wrapTickLabel(scale, index, maxLabelWidth).length;
        return total + Math.max(lineCount * tickLineHeight, dataLabelHeight, barHeight) + ROW_GAP;
    }, 0);

    const padding = toPadding(chart.options.layout.padding);

    return Math.ceil(Math.max(chart.width / DEFAULT_ASPECT_RATIO, rowsHeight + padding.top + padding.bottom));
}

/**
 * Resizes horizontal auto height graphs after each layout. `setHeight` should set the height of the
 * canvas's container, Chart.js then picks up the new size and lays out again.
 */
function createAutoHeightPlugin(isEnabled, setHeight) {
    return {
        id: 'graphAutoHeight',
        afterLayout(chart) {
            if (!isEnabled() || chart.options.indexAxis !== 'y') {
                return;
            }

            const height = calculateHorizontalChartHeight(chart);

            // Only resize on a real change so it settles rather than resizing in a loop
            if (Math.abs(height - chart.height) > 1) {
                setHeight(height);
            }
        },
    };
}

function generateChartScales(direction) {
    if (direction === 'vertical') {
        return generateChartVerticalScales();
    }

    return generateChartHorizontalScales();
}

function generateChartHorizontalScales() {
    return {
        y: {
            beginAtZero: true,
            grid: {
                display: false,
            },
            border: {
                display: true,
                width: 3,
                color: '#000000',
                z: 20,
            },
            ticks: {
                color: '#000',
                // Scriptable so the size follows the breakpoint when the window is resized
                font: () => ({
                    family: 'GTEestiProDisplay, Arial, sans-serif',
                    weight: 400,
                    size: isMobile() ? 14 : 16,
                }),
                autoSkip: false,
                callback: function (value) {
                    // Labels can take up to 40% of the width, the rest is left for the bars
                    return wrapTickLabel(this, value, getAvailableWidth(this.chart) * HORIZONTAL_TICK_MAX_WIDTH);
                },
            },
        },
        x: {
            display: false,
            grid: {
                display: false,
            },
        },
    };
}

function generateChartVerticalScales() {
    return {
        y: {
            display: false,
            grid: {
                display: false,
            },
        },
        x: {
            grid: {
                display: false,
            },
            border: {
                display: true,
                width: 2,
                color: '#000000',
                z: 20,
            },
            ticks: {
                color: '#000',
                // Scriptable so the size follows the breakpoint when the window is resized
                font: () => ({
                    family: 'GTEestiProDisplay, Arial, sans-serif',
                    weight: 300,
                    size: isMobile() ? 14 : 16,
                }),
                autoSkip: false,
                maxRotation: 0,
                callback: function (value) {
                    // Each label gets the width of its bar's slot
                    const slotWidth = getAvailableWidth(this.chart) / Math.max(1, this.chart.data.labels.length);
                    return wrapTickLabel(this, value, slotWidth - VERTICAL_TICK_GAP);
                },
            },
        },
    };
}

function generateDataLabelFontStyles(direction) {
    if (direction === 'vertical') {
        return generateDataLabelFontStylesVertical();
    }

    return generateDataLabelFontStylesHorizontal();
}

function generateDataLabelFontStylesHorizontal() {
    const fontSize = isMobile() ? 14 : 18;

    return {
        family: 'GTEestiProDisplay, Arial, sans-serif',
        weight: 500,
        size: fontSize,
    };
}

function generateDataLabelFontStylesVertical() {
    const fontSize = isMobile() ? 16 : 22;

    return {
        family: 'GTEestiProDisplay, Arial, sans-serif',
        weight: 500,
        size: fontSize,
    };
}
